import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAppDispatch } from "@/hooks/redux-hooks";
import { TDealPipeline } from "@/models/deals.model";
import { createPipeline, updatePipeline } from "@/store/pipelines";
import { getErrorFromBackend } from "@/utils/errors";
import { Loader2, Workflow } from "lucide-react";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";

interface AddStageDialogProps {
  open: boolean;
  onClose: () => void;
  pipelineToEdit?: TDealPipeline;
}

export function CreatePipelineModal({ open, onClose, pipelineToEdit }: AddStageDialogProps) {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [pipelineName, setPipelineName] = useState(pipelineToEdit?.name || "");

  const handleSubmit = async () => {
    try {
      setSaving(true);
      if (pipelineToEdit) {
        await dispatch(updatePipeline(pipelineToEdit.id, pipelineName));
      } else {
        await dispatch(createPipeline(pipelineName));
      }
      enqueueSnackbar("Pipeline has been saved", { variant: "success" });
    } catch (e) {
      enqueueSnackbar(getErrorFromBackend(e));
    } finally {
      setSaving(false);
      onClose();
      setPipelineName("");
    }
  };

  useEffect(() => {
    if (pipelineToEdit) {
      setPipelineName(pipelineToEdit.name);
    }
  }, [pipelineToEdit]);

  return (
    <Dialog open={open} onOpenChange={saving ? undefined : onClose}>
      <DialogContent className="sm:max-w-md rounded-2xl border border-white/[0.08] bg-gradient-to-br from-ink-800/95 to-ink-900/95 shadow-[0_24px_80px_-12px_rgba(0,0,0,0.7)] backdrop-blur-xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-accent-purple/30 to-violet-700/10 ring-1 ring-accent-purple/30">
              <Workflow className="h-4 w-4 text-accent-purple" />
            </div>
            <div className="text-left">
              <DialogTitle className="text-[16px] font-semibold tracking-tight text-white">
                {pipelineToEdit ? "Edit Pipeline" : "Add Pipeline"}
              </DialogTitle>
              <p className="text-[11.5px] text-slate-400">
                {pipelineToEdit
                  ? "Rename this pipeline"
                  : "Create a new pipeline for your deals"}
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="py-2">
          <Label
            htmlFor="stage-name"
            className="mb-1.5 block text-[10.5px] font-semibold uppercase tracking-wider text-slate-500"
          >
            Pipeline Name
          </Label>
          <Input
            id="stage-name"
            placeholder="Enter Pipeline Name..."
            value={pipelineName}
            onChange={(e) => setPipelineName(e.target.value)}
            className="h-10 w-full rounded-xl border-white/[0.06] bg-white/[0.02] px-3 text-[12.5px] text-slate-200 placeholder:text-slate-500 focus:border-brand-500/40 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="inline-flex h-9 items-center rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 text-[12.5px] font-medium text-slate-300 transition hover:border-white/[0.14] hover:text-white disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!pipelineName.trim() || saving}
            onClick={handleSubmit}
            className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-gradient-to-br from-accent-purple to-violet-700 px-4 text-[12.5px] font-semibold text-white shadow-[0_10px_28px_-8px_rgba(168,85,247,0.55)] ring-1 ring-accent-purple/40 transition hover:translate-y-[-1px] disabled:pointer-events-none disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : pipelineToEdit ? (
              "Update Pipeline"
            ) : (
              "Create Pipeline"
            )}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
